import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Truck,
  Users,
  Package,
  FileText,
  DollarSign,
  BarChart3,
  CreditCard,
  Wallet,
  Settings,
  ChevronDown,
  ChevronRight,
  UserCircle,
  Building2,
  Warehouse,
  AlertTriangle,
  Lock,
  ScrollText,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { usePermission } from '../../hooks/usePermission';
import { Permission } from '../../types/entities';

interface MenuItem {
  path: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
  permission?: Permission;
}

interface MenuGroup {
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  items: MenuItem[];
}

interface MenuItemWithTour extends MenuItem {
  tourClass?: string;
}

// Main menu items
const mainMenuItems: MenuItemWithTour[] = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: Home, tourClass: 'nav-dashboard' },
  { path: '/trips', labelKey: 'nav.trips', icon: FileText, tourClass: 'nav-trips' },
  { path: '/expenses', labelKey: 'nav.expenses', icon: DollarSign, tourClass: 'nav-expenses' },
  { path: '/salaries', labelKey: 'nav.salaries', icon: Wallet, roles: ['ADMIN', 'OPERATIONS_MANAGER'], tourClass: 'nav-salaries' },
  { path: '/debt', labelKey: 'nav.debt', icon: CreditCard, tourClass: 'nav-debt' },
  { path: '/reports', labelKey: 'nav.reports', icon: BarChart3, tourClass: 'nav-reports' },
];

// Warehouses group with submenu
const warehousesGroup: MenuGroup = {
  labelKey: 'nav.warehouses',
  icon: Warehouse,
  items: [
    { path: '/warehouses', labelKey: 'nav.warehousesList', icon: Warehouse },
    { path: '/warehouses/manual-receipt', labelKey: 'nav.manualReceipt', icon: Package, permission: 'transaction:manual_receipt' },
    { path: '/warehouses/manual-issue', labelKey: 'nav.manualIssue', icon: Package, permission: 'transaction:manual_issue' },
    { path: '/warehouses/stock-alerts', labelKey: 'nav.stockAlerts', icon: AlertTriangle },
    { path: '/warehouses/period-locks', labelKey: 'nav.periodLocks', icon: Lock, permission: 'period_lock:view' },
    { path: '/warehouses/audit-logs', labelKey: 'nav.auditLogs', icon: ScrollText, permission: 'audit_log:view' },
  ],
};

// Settings group (at the end)
const settingsGroup: MenuGroup = {
  labelKey: 'nav.settings',
  icon: Settings,
  items: [
    { path: '/users', labelKey: 'nav.users', icon: Users, permission: 'user:view' },
    { path: '/vehicles', labelKey: 'nav.vehicles', icon: Truck },
    { path: '/suppliers', labelKey: 'nav.suppliers', icon: Building2 },
    { path: '/customers', labelKey: 'nav.customers', icon: Users },
    { path: '/drivers', labelKey: 'nav.drivers', icon: UserCircle },
    { path: '/items', labelKey: 'nav.items', icon: Package },
  ],
};

export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { can } = usePermission();
  const location = useLocation();

  // Check if any settings item is active
  const isSettingsActive = settingsGroup.items.some(item => location.pathname.startsWith(item.path));
  const [isSettingsOpen, setIsSettingsOpen] = useState(isSettingsActive);

  // Check if any warehouses item is active
  const isWarehousesActive = warehousesGroup.items.some(item => location.pathname.startsWith(item.path));
  const [isWarehousesOpen, setIsWarehousesOpen] = useState(isWarehousesActive);

  const filteredMainItems = mainMenuItems.filter((item) => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  const filteredWarehousesItems = warehousesGroup.items.filter((item) => {
    if (!item.permission) return true;
    return can(item.permission);
  });

  const filteredSettingsItems = settingsGroup.items.filter((item) => {
    if (item.permission && !can(item.permission)) return false;
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  return (
    <aside className="w-64 bg-white shadow-sm min-h-screen">
      <div className="p-6">
        <h1 className="text-xl font-bold text-primary-600">Logistic Manager</h1>
      </div>

      <nav className="px-4 space-y-1">
        {/* Main menu items */}
        {filteredMainItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center px-4 py-3 rounded-lg transition-colors ${item.tourClass || ''} ${
                isActive
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`
            }
          >
            <item.icon className="w-5 h-5 mr-3" />
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}

        {/* Warehouses group - in main menu */}
        <div>
          <button
            onClick={() => setIsWarehousesOpen(!isWarehousesOpen)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
              isWarehousesActive
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center">
              <warehousesGroup.icon className="w-5 h-5 mr-3" />
              <span>{t(warehousesGroup.labelKey)}</span>
            </div>
            {isWarehousesOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {/* Warehouses submenu */}
          {isWarehousesOpen && (
            <div className="ml-4 mt-1 space-y-1">
              {filteredWarehousesItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4 mr-3" />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="my-4 border-t border-gray-200" />

        {/* Settings group */}
        <div>
          <button
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            data-tour="settings"
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
              isSettingsActive
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center">
              <settingsGroup.icon className="w-5 h-5 mr-3" />
              <span>{t(settingsGroup.labelKey)}</span>
            </div>
            {isSettingsOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {/* Settings submenu */}
          {isSettingsOpen && (
            <div className="ml-4 mt-1 space-y-1">
              {filteredSettingsItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4 mr-3" />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>
    </aside>
  );
};
